import { Icon } from '@iconify/react';
import type { AskAgenticAnalyticsResponse } from '@circuitry-ai/doc-data';
import CtyPromptInput, { promptInputType, type AnalystResponse } from '@circuitry-ai/doc-ui';
import { Switch } from '@nextui-org/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalystCard } from './agentic/AnalystCard';

interface AgenticHistoryItem {
  id: string;
  question: string;
  use_cache: boolean;
  response?: AskAgenticAnalyticsResponse;
  error?: string;
  loading?: boolean;
}

interface AgenticAnalyticsDetailsProps {
  readonly history: AgenticHistoryItem[];
  readonly name?: string;
  readonly useCache: boolean;
  readonly onUseCacheChange: (value: boolean) => void;
  readonly onAsk: (question: string) => void;
  readonly onDrilldown: (question: string) => void;
  readonly onClose: () => void;
  /** When false, hides the panel close control (e.g. Decision analysts). Defaults to true. */
  readonly showCloseButton?: boolean;
  /** Kept for API compatibility; sizing is now controlled by the parent layout. */
  readonly isSplitView?: boolean;
}

export default function AgenticAnalyticsDetails({
  history,
  name,
  useCache,
  onUseCacheChange,
  onAsk,
  onDrilldown,
  onClose,
  showCloseButton = true,
}: Readonly<AgenticAnalyticsDetailsProps>) {
  const { t } = useTranslation();
  const endOfHistoryRef = useRef<HTMLDivElement | null>(null);
  const [isSpeechMode, setIsSpeechMode] = useState(false);

  const isLoading = history.some((item) => item.loading);

  useEffect(() => {
    endOfHistoryRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history.length]);

  return (
    /* Single outer card wrapping the entire agentic panel */
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-default-200 bg-content1">
      {showCloseButton ? (
        <div className="absolute right-1 top-1 z-10">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-default-500 hover:bg-default-100/80"
            onClick={onClose}
            aria-label={t('Close response panel')}
          >
            <Icon icon="material-symbols:close-rounded" width={18} />
          </button>
        </div>
      ) : null}

      {/* Scrollable Q&A history — items use dividers, not individual card borders */}
      <div className="flex-1 overflow-y-auto px-4 pt-2">

        <div className={`flex items-start justify-between gap-3 p-2 border-b border-divider flex-shrink-0 ${showCloseButton ? 'pr-8' : ''}`}>
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-sm font-semibold text-default-700">{name || 'Analyst'}</span>
            <span className="text-xs text-default-500">
              <span className="inline-flex items-center rounded-full bg-success h-2 w-2 mr-2" />
              {t('Active now')}
            </span>
          </div>
          <Switch
            size="sm"
            isSelected={useCache}
            onValueChange={onUseCacheChange}
            aria-label={t('Use cache')}
            classNames={{ base: 'flex-shrink-0' }}
          >
            {t('Use cache')}
          </Switch>
        </div>

        {history.map((item) => (
          <AnalystCard
            key={item.id}
            id={item.id}
            question={item.question}
            useCache={item.use_cache}
            response={item.response as AnalystResponse | undefined}
            error={item.error}
            loading={item.loading}
            unstyled
            onDrilldown={onDrilldown}
          />
        ))}
        <div ref={endOfHistoryRef} />
      </div>

      {/* Inline ask bar — sticks to bottom of the card via flex column */}
      <div className="shrink-0 border-t border-default-200 bg-content1 px-4 py-3">
        <CtyPromptInput
          id="agentic-inline-ask"
          elementType={promptInputType.INPUT}
          enableSearch={true}
          enableSendBtn={true}
          enableSpeak={false}
          handleSendClick={onAsk}
          placeholder={t('Ask a follow-up question')}
          disabled={isLoading}
          inputHeight="ctyPromptInput"
          isSpeechMode={isSpeechMode}
          setIsSpeechMode={setIsSpeechMode}
        />
      </div>
    </div>
  );
}
